import { useEffect, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AuthFrame, AuthHead, Notice } from "@/components/AuthFrame";
import { Icon } from "@/components/Icon";
import { ActivationSteps } from "@/features/auth/ActivationSteps";
import { AuthenticatorLink } from "@/features/auth/AuthenticatorLink";
import { CodeInput } from "@/features/auth/CodeInput";
import { PasswordFields } from "@/features/auth/PasswordFields";
import { RecoveryCodes } from "@/features/auth/RecoveryCodes";
import { authErrorText, newPasswordProblem } from "@/features/auth/errors";
import { signedIn, useGo } from "@/features/auth/afterSignIn";
import { languageChosen, showLanguage } from "@/i18n";
import { ApiError } from "@/lib/api";
import { invitationConfirm, invitationPassword, openInvitation, type Invitation, type Me, type NewSecret } from "@/lib/auth";
import { forgetLinkToken, useLinkToken } from "@/lib/linkToken";
import { session } from "@/lib/session";

// The first time, from the invitation mail (I-1 … I-4): a password, the app linked, a first code from it,
// and the eight recovery codes — kept before going on. The link works once and seven days; left halfway,
// it starts again at the password (a reload makes a new secret, docs/api.md §9.26).

export const Route = createFileRoute("/invitation")({
  beforeLoad: () => session.ended(),
  component: InvitationScreen,
});

type Step = "loading" | "invalid" | "password" | "app" | "code" | "codes";

function InvitationScreen() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const go = useGo();
  const token = useLinkToken("invitation");
  const [step, setStep] = useState<Step>(token ? "loading" : "invalid");
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [secret, setSecret] = useState<NewSecret | null>(null);
  const [code, setCode] = useState("");
  const [done, setDone] = useState<(Me & { recoveryCodes: string[] }) | null>(null);
  const [kept, setKept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    openInvitation(token)
      .then((answer) => {
        setInvitation(answer);
        // In the language Willems chose for them, unless they chose one on this browser.
        if (!languageChosen()) showLanguage(answer.language, false);
        setStep("password");
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.code === "LINK_INVALID") setStep("invalid");
        else setError(authErrorText(t, err, i18n.language));
      });
  }, [token, t, i18n.language]);

  const fail = (err: unknown) => {
    if (err instanceof ApiError && err.code === "LINK_INVALID") return setStep("invalid");
    setError(authErrorText(t, err, i18n.language));
  };

  const choosePassword = async () => {
    const problem = newPasswordProblem(t, password, repeat, invitation?.email ?? null);
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);
    try {
      setSecret(await invitationPassword(token!, password));
      setPassword("");
      setRepeat("");
      setStep("app");
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (value = code) => {
    if (value.length !== 6 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const answer = await invitationConfirm(token!, value);
      forgetLinkToken("invitation");
      setDone(answer);
      setStep("codes");
    } catch (err) {
      setCode("");
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const activating = t("activate.label");

  if (step === "invalid") {
    return (
      <AuthFrame label="Portaal Uitnodiging verlopen" step={activating}>
        <AuthHead title={t("link.invalidTitle")} lead={t("link.invalidInvitation")} />
        <div className="wl-auth__actions">
          <span className="wl-spacer" />
          <Link to="/login" className="wl-btn wl-btn--primary wl-btn--lg">
            {t("link.toSignIn")}
          </Link>
        </div>
      </AuthFrame>
    );
  }

  if (step === "loading" || !invitation) {
    return (
      <AuthFrame label="Portaal Uitnodiging" step={activating}>
        {error ? <Notice tone="danger">{error}</Notice> : <span className="wl-skeleton" style={{ height: "var(--space-24)" }} />}
      </AuthFrame>
    );
  }

  if (step === "password") {
    return (
      <AuthFrame label="Portaal Eerste keer wachtwoord" step={activating}>
        <ActivationSteps current={0} />
        <form
          className="wl-vstack"
          style={{ gap: "var(--space-5)", alignItems: "stretch" }}
          onSubmit={(event) => {
            event.preventDefault();
            void choosePassword();
          }}
        >
          <AuthHead
            title={t("activate.passwordTitle")}
            lead={t("activate.passwordLead", { email: invitation.email, companies: invitation.companies.join(", ") })}
          />
          {invitation.next === "app" && <Notice tone="info">{t("activate.startedBefore")}</Notice>}
          {error && <Notice tone="danger">{error}</Notice>}
          <PasswordFields
            password={password}
            repeat={repeat}
            onPassword={setPassword}
            onRepeat={setRepeat}
            email={invitation.email}
            label={t("login.password")}
            autoFocus
          />
          <p className="t-body-sm t-muted">{t("activate.passwordTip")}</p>
          <div className="wl-auth__actions">
            <span className="wl-spacer" />
            <button type="submit" className="wl-btn wl-btn--primary wl-btn--lg" disabled={busy || password === ""}>
              {t("activate.nextApp")}
            </button>
          </div>
        </form>
      </AuthFrame>
    );
  }

  if (step === "app" && secret) {
    return (
      <AuthFrame label="Portaal Eerste keer app koppelen" step={activating} wide>
        <ActivationSteps current={1} />
        <AuthHead title={t("app.title")} lead={t("app.lead")} />
        <AuthenticatorLink secret={secret.secret} otpauthUri={secret.otpauthUri} email={invitation.email} />
        <div className="wl-auth__actions">
          <button type="button" className="wl-btn wl-btn--link wl-no-print" onClick={() => window.print()}>
            <Icon name="printer" />
            {t("app.print")}
          </button>
          <span className="wl-spacer" />
          <button type="button" className="wl-btn wl-btn--primary wl-btn--lg" onClick={() => setStep("code")}>
            {t("activate.nextCode")}
          </button>
        </div>
      </AuthFrame>
    );
  }

  if (step === "code") {
    return (
      <AuthFrame label="Portaal Eerste keer code" step={activating}>
        <ActivationSteps current={2} />
        <form
          className="wl-vstack"
          style={{ gap: "var(--space-5)", alignItems: "stretch" }}
          onSubmit={(event) => {
            event.preventDefault();
            void confirm();
          }}
        >
          <AuthHead title={t("activate.codeTitle")} lead={t("code.leadApp")} />
          {error && <Notice tone="danger">{error}</Notice>}
          <CodeInput value={code} onChange={setCode} onComplete={(value) => void confirm(value)} label={t("code.label")} invalid={Boolean(error)} autoFocus />
          <p className="t-body-sm t-muted">{t("code.changes")}</p>
          <Notice tone="info">{t("code.timeTip")}</Notice>
          <div className="wl-auth__actions">
            <button type="button" className="wl-btn wl-btn--ghost" onClick={() => setStep("app")}>
              {t("code.scanAgain")}
            </button>
            <span className="wl-spacer" />
            <button type="submit" className="wl-btn wl-btn--primary wl-btn--lg" disabled={busy || code.length !== 6}>
              {t("code.confirm")}
            </button>
          </div>
        </form>
      </AuthFrame>
    );
  }

  if (step === "codes" && done) {
    return (
      <AuthFrame label="Portaal Eerste keer herstelcodes" step={activating}>
        <ActivationSteps current={3} />
        <Notice tone="success">{t("activate.linked")}</Notice>
        <AuthHead title={t("codes.title")} lead={t("codes.lead")} />
        <RecoveryCodes codes={done.recoveryCodes} email={done.user.email} kept={kept} onKept={setKept} />
        <div className="wl-auth__actions">
          <span className="wl-auth__foot" style={{ flex: "1 1 auto" }}>
            {t("codes.noneLeft")}
          </span>
          <button type="button" className="wl-btn wl-btn--primary wl-btn--lg" disabled={!kept} onClick={() => void go(signedIn(queryClient, withoutCodes(done)))}>
            {t("activate.toPortal")}
            <Icon name="chevronRight" />
          </button>
        </div>
      </AuthFrame>
    );
  }

  return null;
}

/** What is kept of who signed in: never the recovery codes. */
function withoutCodes({ recoveryCodes: _codes, ...me }: Me & { recoveryCodes: string[] }): Me {
  return me;
}
